import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Theme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import MUIAccordion, { AccordionProps } from '@mui/material/Accordion';
import MUIAccordionDetails from '@mui/material/AccordionDetails';
import MUIAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import styled from '@mui/material/styles/styled';

import { TwButton, TwButtonType } from '../../../components/TwButtons/TwButtons';
import TwInitialsIndicator from '../../../components/TwInitialsIndicator/TwInitialsIndicator';
import TwDocumentViewer from '../../../components/TwDocumentViewer/TwDocumentViewer';
import { PublicLoadTenant } from '../../../providers/Guest/Guest.model';
import useFeatureFlags from '../../../providers/FeatureFlags/FeatureFlags.provider';
import StringUtils from '../../../utils/stringUtils';
import LoadUtils from '../../../utils/loadUtils';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import useFormatUrlsInText from '../../../hooks/useFormatUrlText';
import { useParams } from 'react-router-dom';

import TwAlert from '../../../components/TwAlerts/TwAlerts';
import DetailFile from '../../../components/TwLoadDetail/DetailFile/DetailFile';
import NumberUtils from '../../../utils/numberUtils';
import TwGrossPayTooltip from '../../../components/TwGrossPayTooltip/TwGrossPayTooltip';
import useAuth from '../../../providers/Auth/Auth.provider';
import { TabPanelProps } from '../PublicLoad';
import {
  TwLoadDetailTab,
  TwLoadDetailTabsGroup
} from '../../../components/TwLoadDetail/components/LoadTabs/TwLoadDetailTabs';
import RouteInfo from '../../../components/TwLoadDetail/components/RouteInfo/RouteInfo';
import { DetailLoadPhoto, LOAD_TYPE, LoadDocumentType } from '../../../redux/api/load/load.model';
import CircularProgress from '@mui/material/CircularProgress';
import FileUtils from '../../../utils/fileUtils';
import DetailPhoto from '../../../components/TwLoadDetail/DetailPhoto/DetailPhoto';
import { logRocketTrack } from '../../../utils/logRocketUtils';
import TwDeleteModal from '../../../components/TwDeleteModal/TwDeleteModal';
import Chip from '@mui/material/Chip/Chip';
import TwDispatchNotes, {
  DispatchNoteType
} from '../../../components/TwDispatchNotes/TwDispatchNotes';
import { TwLoadCommodityReadOnly } from '../../../components/Commodities/TwLoadCommodities/TwLoadCommodities';
import useScrollToView from '../../../hooks/useScrollToView';
import DriverPayUtils from '../../../utils/driverPayUtils';

import {
  ORDER_STATUS,
  Order,
  OrderAttachment,
  OrdersTripsDocumentParams,
  SharedLinkTenant
} from '../../../redux/api/orders/orders.model';
import TwOrderTripsDocViewer from '../../../components/TwOrderTripsDocViewer/TwOrderTripsDocViewer';
import {
  useDeleteOrderTripDocumentByTokenMutation,
  useUploadOrderTripDocumentByTokenMutation
} from '../../../redux/api/orders/ordersApi';
import TwUploadOrderTripFiles from '../../../components/TwUploadOrderTripFiles/TwUploadOrderTripFiles';
import TwDeleteOrderTripDoc from '../../../components/TwUploadOrderTripFiles/TwDeleteOrderTripDoc';
import TwScanner from '../../../components/TwScanner/TwScanner';
import { TwSelectOption } from '../../../components/TwSelects/TwSelects';
import UploadBol from '../../UploadBol/UploadBol';
import { USER_ROLE } from '../../../providers/User/User.model';
import { Trip, TripDriver } from '../../../redux/api/trips/trips.model';
import LoadRateAndTotalRates from '../../../components/TwLoadDetail/components/LoadRates/LoadRateAndTotalRates';
import { EntityType } from '../../../redux/Global.model';
import { useAppSelector } from '../../../redux/hooks';
import DocumentUtils from '../../../utils/documentUtils';
import TwLoadTags from '../../../components/TwLoadTag/TwLoadTag';
import TwLoadStatus from '../../../components/TwLoadStatus/TwLoadStatus';
import { ClientResponse } from '../../../redux/api/customer/customer.model';

export const DROP_ZONE_HIGHLIGHTED_TEXT = 'Upload document';
export const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB
export const MAX_PHOTOS = 5;

export interface FullPublicRouteDetailProps {
  order: Order;
  primaryClient: ClientResponse | null;
  tenant?: PublicLoadTenant | SharedLinkTenant;

  driverId?: string;
  reloadData?: () => Promise<void>;
  isDriverAssignedToTrip?: boolean;
  trip?: Trip;
}

interface IExpandedItems {
  [key: string]: boolean;
}

const EXPANDED_ITEMS: IExpandedItems = {
  route: true,
  commodity: true,
  instructions: true,
  details: true,
  equipment: true,
  attachments: true,
  overview: true,
  photos: true
};

const isWebInFlutter = (): boolean => {
  return navigator.userAgent.includes('Flutter');
};
const FullPublicRouteDetail: FC<FullPublicRouteDetailProps> = ({
  order,
  primaryClient,
  tenant,
  driverId,
  trip,
  reloadData,
  isDriverAssignedToTrip
}) => {
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const { enableDynamicWebTwain, enableDriverGrossPayEstimate, enableFlexibleTagging } =
    useFeatureFlags();
  const { addNotification } = useNotifications();
  const formatUrlsInText = useFormatUrlsInText();
  const [scrollIntoView] = useScrollToView();
  const [uploadOrderTripDocumentByToken] = useUploadOrderTripDocumentByTokenMutation();
  const [deleteDocumentByToken] = useDeleteOrderTripDocumentByTokenMutation();

  const { token } = useParams();
  const {
    data: { userAuthorized: isAuth, userData },
    updateUserData
  } = useAuth();
  const isDriver = userData?.role === USER_ROLE.DRIVER;
  const isCarrier = userData?.role === USER_ROLE.CARRIER;
  const isDriverOrCarrier = isDriver || isCarrier;
  const isCustomerContact = userData?.role === USER_ROLE.CUSTOMER_CONTACT;

  const dispatchNotesRef = React.useRef<HTMLDivElement>(null);
  const [isTwScannerOpen, setIsTwScannerOpen] = useState<boolean>(false);
  const [isEnabledSeeDocs, setIsEnabledSeeDocs] = useState<boolean>(true);
  const [isEnabledCarrier, setIsEnabledCarrier] = useState<boolean>(true);
  const [isEnabledDispatcher, setIsEnabledDispatcher] = useState<boolean>(true);

  const [isEnabledAdvanceDriver, setIsEnabledAdvanceDriver] = useState<boolean>(false);
  const [isEnabledGrossPayOnDispatch, setIsEnabledGrossPayOnDispatch] = useState(false);
  const [isDivisionEnable, setIsDivisionEnable] = useState<boolean>(false);
  const [showCustomer, setShowCustomer] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [loadTabs, setLoadTabs] = useState<number>(0);
  const [documentsOpen, setDocumentsOpen] = useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<OrderAttachment | null>(null);
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [files] = useState<File[]>([]);
  const [fileUploadErrors, setFileUploadErrors] = useState<string[]>([]);
  const [expandedItems, setExpandedItems] = useState<IExpandedItems>(EXPANDED_ITEMS);
  const [docViewOpen, setDocViewOpen] = useState(false);
  const [fileToUpload, setFileToUpload] = useState<File>();
  const [browserOnline, setBrowserOnline] = useState<boolean>(true);
  const isLtlEnabled = useAppSelector((state) => state.user.isLtlEnabled);

  const commoditiesEnabled =
    (userData?.tenantSettings && userData.tenantSettings['load-commodities']) || false;
  const showCustomerOrderAttachments =
    userData?.tenantSettings && userData.tenantSettings['show-customer-order-attachments'];
  const [matchesMobileOrFlutterApp, setMatchesMobileOrFlutterApp] = useState<boolean>(false);

  useEffect(() => {
    setMatchesMobileOrFlutterApp(matchesMobileOrTablet || isWebInFlutter());
  }, []);

  useEffect(() => {
    if (!tenant) return;

    const { tenantSettings } = tenant;

    setIsEnabledSeeDocs(tenantSettings?.allow_drivers_to_view_documents);
    setIsEnabledCarrier(tenantSettings?.['multiple-carriers']);
    setIsEnabledDispatcher(tenantSettings?.['multiple-dispatchers']);
    setIsEnabledAdvanceDriver(tenantSettings?.['advanced-driver-fields']);
    setIsEnabledGrossPayOnDispatch(tenantSettings?.['gross-pay-on-dispatch']);
    setIsDivisionEnable(tenantSettings?.['divisions']);
    setShowCustomer(tenantSettings?.['show-customer-contact-on-dispatch']);

    if (!isAuth) {
      updateUserData({
        tenant: tenant,
        tenantSettings,
        short_offset: tenant.short_offset
      });
    }
  }, [tenant]);

  // Handle single file uploading.
  useEffect(() => {
    if (files.length > 0) {
      (async (): Promise<void> => {
        try {
          if (!order || !token) return;

          const document: OrdersTripsDocumentParams = {
            document: files[0],
            type: LoadDocumentType.rateConfirmation,
            entity: 'order',
            orderId: order.id,
            visibility: true
          };
          const response = await uploadOrderTripDocumentByToken({
            document: document,
            token
          }).unwrap();

          if (response === 200) {
            setFileUploadErrors([]);
            reloadData && (await reloadData());
          }
        } catch (error) {
          alert(error);
          setFileUploadErrors(["Couldn't upload file"]);
        }
      })();
    }
  }, [files]);

  useEffect(() => {
    const online = navigator.onLine;
    if (!online) {
      setBrowserOnline(false);
    } else {
      setBrowserOnline(true);
    }
  }, [navigator.onLine]);

  useEffect(() => {
    if ((loadTabs === 1 || currentTab === 2) && matchesMobileOrTablet) {
      scrollIntoView(dispatchNotesRef, 'start');
    }
  }, [loadTabs, currentTab, matchesMobileOrTablet]);

  const handleAccordionExpandedChange = (key: string): void => {
    const currentExpanded = expandedItems[key];
    setExpandedItems((currentExpandedItems) => ({
      ...currentExpandedItems,
      [key]: !currentExpanded
    }));
  };

  const handleChangeUploadDocument = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e?.target?.files !== null) {
      if (e.target.files[0].size > MAX_FILE_SIZE) {
        logRocketTrack('Public link', {
          action: 'Upload document',
          severity: 'low',
          tenant_id: userData?.tenant?.id,
          company_name: userData?.tenant?.company_name,
          email: userData?.email_address,
          user_first_name: userData?.first_name,
          user_last_name: userData?.last_name
        });
        addNotification({
          message: 'Error: maximum attachment size 15 mb',
          variant: 'error',
          position: 'bottomCenter'
        });
        return;
      }
      setFileToUpload(e.target.files[0]);
      e.target.value = '';
      setDocumentsOpen(true);
    }
  };

  const handleDocViewerOpen = (
    isDocumentVisible: boolean | undefined,
    doc: OrderAttachment
  ): void => {
    if (isDocumentVisible || (isDocumentVisible === undefined && isEnabledSeeDocs)) {
      setDocViewOpen(true);
      setSelectedDocument(doc);
    }
  };

  const handleDocumentsUpload = async (): Promise<void> => {
    reloadData && (await reloadData());
  };

  const shouldShowGrossPay = (driver: TripDriver): boolean => {
    const isGrossPaySettingsOn: boolean =
      enableDriverGrossPayEstimate && isEnabledAdvanceDriver && isEnabledGrossPayOnDispatch;
    const isGrossPayPresent =
      !!driver.driver_pay_override_data ||
      DriverPayUtils.getOtherPayItemsTotalAmount(driver.trip_driver_pay_other_payment || []) > 0;
    const isDriverLink = !!driverId && driverId === driver.id;
    return isGrossPaySettingsOn && isGrossPayPresent && isDriverLink;
  };

  // We need to filter docs to preview with visibility for drivers,
  // otherwise drivers can see files while on preview by clicking on arrow right,
  // customer can see all files.
  const filesToPreview = useMemo(() => {
    if (driverId && isDriverAssignedToTrip) {
      return order?.order_attachments?.filter((doc) => doc.visibility) ?? [];
    } else if (showCustomerOrderAttachments) {
      return order?.order_attachments?.filter((doc) => doc.order_id) ?? [];
    } else {
      return order?.order_attachments ?? [];
    }
  }, [
    driverId,
    isDriverAssignedToTrip,
    order?.order_attachments,
    isCustomerContact,
    showCustomerOrderAttachments
  ]);

  // Doc and photo delete
  const handleCloseDeleteDoc = (): void => {
    setShowDelete(false);
    setSelectedDocument(null);
  };

  const handleDeleteDoc = (): void => {
    order?.id &&
      token &&
      selectedDocument &&
      deleteDocumentByToken({ id: selectedDocument.id, token })
        .unwrap()
        .then(() => {
          reloadData && reloadData();
          setShowDelete(false);
        });
  };

  const openScanner = (): void => {
    if (!browserOnline) {
      logRocketTrack('Customer public link', {
        action: 'Network error',
        severity: 'high',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: 'Network error. Please connect to the internet and try again.',
        variant: 'error',
        position: 'bottomCenter'
      });

      return;
    }
    setIsTwScannerOpen(true);
  };

  const closeScanner = (documentUploaded: boolean): void => {
    setIsTwScannerOpen(false);

    if (!documentUploaded) {
      logRocketTrack('Upload', {
        action: 'Upload document on public link',
        severity: 'medium',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: 'No document uploaded',
        position: 'bottomCenter',
        variant: 'error'
      });
    }

    // progress if scanner was opened for BOL upload
    /* TODO: if (isBolWarningShown) {
      handleStatusProgression();
      setIsBolUploadOpen(false);
    } */
  };

  const handleLoadDetailsTabChange = (_event: React.SyntheticEvent, newValue: number): any => {
    setLoadTabs(newValue);
  };

  const handleDownloadDoc = async (doc: OrderAttachment): Promise<void> => {
    try {
      await DocumentUtils.handleOrderDownloadDoc(order, doc);
    } catch {
      addNotification({
        message: "Couldn't download attachment",
        variant: 'error'
      });
    }
  };

  const tripOptionsForUpload = useMemo(() => {
    if (!trip) return [] as TwSelectOption[];

    const options = [] as any[];

    if (trip) {
      options.push({
        value: trip.id || '',
        label: isLtlEnabled
          ? `${trip.readable_id}`
          : `${order.readable_id} - ${trip.sort_order + 1}`,
        customer: isLtlEnabled
          ? `${trip.readable_id}`
          : `${order.readable_id} - ${trip.sort_order + 1}`
      });
    }

    options.push({
      value: '',
      label: '-',
      customer: 'Do not assign trip'
    });

    return options;
  }, [trip]);

  const showAttachmentsSection = (): JSX.Element[] => {
    return (
      order &&
      order.order_attachments
        ?.filter((doc) => !doc.is_generated && doc.order_id)
        .map((document) => (
          <DetailFile
            key={document.id}
            onClick={(): void => {
              handleDocViewerOpen(true, document);
            }}
            fileName={document.attachment.name}
            type={StringUtils.getLoadDocumentTypeLabel(document.type)}
            created_at={document.created_at ? new Date(document.created_at) : undefined}
            onDownload={(): void => {
              handleDownloadDoc(document);
            }}
            showMenu={false}
            size={document.attachment.size}
            showVisibility={false}
            isEncrypted={document.is_encrypted}
            fileType={document.attachment.file_type}
            fileKey={document.attachment.key}
            attachmentId={document.id}
          />
        ))
    );
  };

  const showGeneralDataSectionDesktop = (): JSX.Element => {
    if (driverId && isDriverAssignedToTrip) {
      return (
        <Box component="div" className="load-data-container__general-data">
          <TwLoadDetailTabsGroup
            value={loadTabs}
            onChange={handleLoadDetailsTabChange}
            aria-label="TwTabs"
            role="TwTabs"
          >
            <TwLoadDetailTab label="Order overview" />
          </TwLoadDetailTabsGroup>
          <TabPanel id="load-detail-tab" value={loadTabs} index={0}>
            <>
              <Box component="div" className="title">
                Route
              </Box>
              {order && (
                <RouteInfo
                  load={order}
                  showStatusUpdateInput={true}
                  showStatusReadOnly
                  isCustomerLink={false}
                  showAddSignatureAction={false}
                  publicLoadToken={token}
                  onSignatureSaved={(): void => {
                    reloadData && reloadData();
                  }}
                  showCheckInAndOutTimes={false}
                />
              )}

              {order && order.order_commodities?.length > 0 && commoditiesEnabled && (
                <TwLoadCommodityReadOnly
                  commodities={order.order_commodities}
                  entity={EntityType.ORDER}
                />
              )}

              <Box component="div" className="title">
                Order notes
              </Box>
              <Box component="div" className="special-instructions">
                {order && formatUrlsInText(order.instructions || '')}
              </Box>

              <Box component="div" className="title">
                {`${order?.order_attachments.length || 0} Attachments`}
              </Box>
              <Box component="div" className="attachments">
                {driverId && isDriverAssignedToTrip && (
                  <>
                    <Box
                      className="attachments--container-2"
                      onClick={(): void =>
                        matchesMobileOrFlutterApp ? openScanner() : setDocumentsOpen(true)
                      }
                    >
                      <CloudUploadIcon className="attachments--icon" sx={{ marginRight: '10px' }} />
                      <p className="attachments--help-text">
                        <span className="attachments--highlited-text">
                          {DROP_ZONE_HIGHLIGHTED_TEXT}
                        </span>
                      </p>
                    </Box>

                    {fileUploadErrors.length > 0 &&
                      fileUploadErrors.map((error, i) => <TwAlert key={i} message={error} />)}
                  </>
                )}

                {order &&
                  order.order_attachments?.map((document, i) => (
                    <DetailFile
                      key={i}
                      fileName={document.attachment.name}
                      type={StringUtils.getLoadDocumentTypeLabel(document.type)}
                      created_at={
                        document.attachment.created_at
                          ? new Date(document.attachment.created_at)
                          : undefined
                      }
                      onClick={(): void => {
                        handleDocViewerOpen(
                          !driverId || !isDriverAssignedToTrip ? true : document.visibility,
                          document
                        );
                      }}
                      isEncrypted={document.is_encrypted}
                      tag={
                        document.trip_tag
                          ? isLtlEnabled
                            ? `Trip #${document.trip_tag.readable_id}`
                            : `Trip #${order.readable_id}-${document.trip_tag.trip_sort_order + 1}`
                          : undefined
                      }
                    />
                  ))}
              </Box>
            </>
          </TabPanel>
        </Box>
      );
    } else {
      return (
        <Box component="div" className="load-data-container__general-data">
          <Box component="div" className="title">
            Route
          </Box>
          {order && (
            <RouteInfo
              load={order}
              showStatusUpdateInput={true}
              showStatusReadOnly
              isCustomerLink={!driverId}
              showAddSignatureAction={
                order.order_type !== LOAD_TYPE.SEGMENT && !!driverId && isDriverAssignedToTrip
              }
              publicLoadToken={token}
              onSignatureSaved={(): void => {
                reloadData && reloadData();
              }}
              showCheckInAndOutTimes={false}
            />
          )}
          {showCustomerOrderAttachments && (
            <>
              <Box component="div" className="title">
                Attachments
              </Box>
              <Box sx={{ marginTop: '12px' }}>{showAttachmentsSection()}</Box>
            </>
          )}

          {order && order.order_commodities?.length > 0 && commoditiesEnabled && (
            <TwLoadCommodityReadOnly
              commodities={order.order_commodities}
              entity={EntityType.ORDER}
            />
          )}
        </Box>
      );
    }
  };

  const showTabsContainerMobile = (): JSX.Element => {
    if (driverId && isDriverAssignedToTrip) {
      return (
        <Box component="section" className="tabs-container" sx={{ paddingLeft: '1.25rem' }}>
          <TwLoadDetailTabsGroup
            value={loadTabs}
            onChange={handleLoadDetailsTabChange}
            aria-label="TwTabs"
            role="TwTabs"
          >
            <TwLoadDetailTab label="Order overview" />
          </TwLoadDetailTabsGroup>

          <TabPanel id="load-detail-tab" value={loadTabs} index={0}>
            <>
              <Accordion
                expanded={!!expandedItems['route']}
                onChange={(): void => handleAccordionExpandedChange('route')}
              >
                <AccordionSummary aria-controls="route-content">Route</AccordionSummary>
                <AccordionDetails>
                  {order && (
                    <RouteInfo
                      load={order}
                      showStatusUpdateInput={false}
                      isCustomerLink={!driverId}
                      showAddSignatureAction={
                        order.order_type !== LOAD_TYPE.SEGMENT &&
                        !!driverId &&
                        isDriverAssignedToTrip
                      }
                      publicLoadToken={token}
                      onSignatureSaved={(): void => {
                        reloadData && reloadData();
                      }}
                      showCheckInAndOutTimes={false}
                    />
                  )}
                </AccordionDetails>
              </Accordion>

              {order && order.order_commodities?.length > 0 && commoditiesEnabled && (
                <Accordion
                  expanded={!!expandedItems['commodity']}
                  onChange={(): void => handleAccordionExpandedChange('commodity')}
                >
                  <AccordionSummary aria-controls="commodity-content">Commodity</AccordionSummary>
                  <AccordionDetails>
                    <TwLoadCommodityReadOnly
                      commodities={order.order_commodities}
                      showTitle={false}
                      entity={EntityType.ORDER}
                    />
                  </AccordionDetails>
                </Accordion>
              )}

              <Accordion
                expanded={!!expandedItems['attachments']}
                onChange={(): void => handleAccordionExpandedChange('attachments')}
              >
                <AccordionSummary aria-controls="attachments-content">
                  {`${order?.order_attachments.length || 0} Attachments`}
                </AccordionSummary>
                <AccordionDetails>
                  <Box component="div" className="attachments">
                    {order &&
                      order.order_attachments?.map((document, i) => (
                        <DetailFile
                          key={i}
                          fileName={document.attachment.name}
                          type={StringUtils.getLoadDocumentTypeLabel(document.type)}
                          created_at={
                            document.attachment.created_at
                              ? new Date(document.attachment.created_at)
                              : undefined
                          }
                          onClick={(): void => {
                            handleDocViewerOpen(!driverId ? true : document.visibility, document);
                          }}
                          isEncrypted={document.is_encrypted}
                        />
                      ))}
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={!!expandedItems['details']}
                onChange={(): void => handleAccordionExpandedChange('details')}
              >
                <AccordionSummary aria-controls="details">Overview</AccordionSummary>
                <AccordionDetails>
                  {enableFlexibleTagging && (
                    <>
                      <Box component="div" className="overview--sub-title">
                        Tags
                      </Box>
                      <TwLoadTags
                        entity={EntityType.TRIP}
                        loadId={order?.id || ''}
                        tripId={trip?.id || ''}
                        tags={order?.tags || []}
                        readOnly
                        containerSx={{ marginBottom: '1rem' }}
                      />
                    </>
                  )}

                  {showCustomer && order && order.raw_customer && (
                    <>
                      <Box component="div" className="overview--sub-title">
                        Customer
                      </Box>
                      <Box component="div" className="overview__broker">
                        <TwInitialsIndicator
                          text={(order.raw_customer?.initials ?? order.raw_customer?.name) || ''}
                          displayRawText={!order?.raw_customer?.initials}
                        />
                        <Box component="div" className="overview__broker--data">
                          <Box component="span">{order.raw_customer?.name}</Box>
                          <Box component="span">
                            {order.raw_customer?.primary_phone
                              ? StringUtils.formatPhoneNumber(
                                  order.raw_customer?.primary_phone.toString()
                                )
                              : order.raw_customer?.billing_email}
                          </Box>
                        </Box>
                      </Box>
                    </>
                  )}

                  {order && showCustomer && order.raw_customer?.contact && (
                    <>
                      <Box component="div" className="overview--sub-title">
                        Customer contact
                      </Box>
                      <Box component="div" className="overview__broker">
                        <TwInitialsIndicator
                          text={order.raw_customer.contact?.name || ''}
                          displayRawText={!order.raw_customer.contact?.name}
                        />
                        <Box component="div" className="overview__broker--data">
                          <Box component="span">{order.raw_customer.contact?.name}</Box>
                          <Box component="span">
                            {order.raw_customer.contact?.phone
                              ? StringUtils.formatPhoneNumber(
                                  order.raw_customer.contact?.phone.toString()
                                )
                              : order.raw_customer.contact.email_address}
                          </Box>
                        </Box>
                      </Box>
                    </>
                  )}

                  {isDivisionEnable && order && order.division && (
                    <>
                      <Box component="div" className="overview--sub-title">
                        Division
                      </Box>
                      <Box component="div" className="overview__broker">
                        <TwInitialsIndicator
                          text={order.division.name || ''}
                          displayRawText={!order.division.name}
                        />
                        <Box component="div" className="overview__broker--data">
                          <Box component="span">{order.division.name || ''}</Box>
                          <Box component="span">
                            {order.division.phone
                              ? StringUtils.formatPhoneNumber(order.division.phone.toString())
                              : order.division.email_address}
                          </Box>
                        </Box>
                      </Box>
                    </>
                  )}

                  <>
                    <Box component="div" className="overview--sub-title">
                      Order status
                    </Box>
                    <Box component="div" className="overview__status">
                      {StringUtils.getOrderStatusLabel(order?.status || ORDER_STATUS.NOT_STARTED)}
                    </Box>
                  </>

                  {order?.customer_order_number && (
                    <>
                      <Box component="div" className="overview--sub-title">
                        Customer order #
                      </Box>
                      <Box component="div" className="overview__client-load">
                        {order.customer_order_number}
                      </Box>
                    </>
                  )}

                  <Box component="div" className="overview--sub-title">
                    Order #
                  </Box>
                  <Box component="div" className="overview__tw-load">
                    {order && order.readable_id}
                  </Box>

                  {order &&
                    order.dispatcher &&
                    isEnabledDispatcher &&
                    (order.dispatcher.first_name || order.dispatcher.last_name) !== '' && (
                      <>
                        <Box component="div" className="overview--sub-title">
                          Dispatcher
                        </Box>
                        <Box component="div" className="overview__client-load">
                          {order.dispatcher.first_name} {order.dispatcher.last_name}
                        </Box>
                      </>
                    )}
                </AccordionDetails>
              </Accordion>

              <>
                {driverId && (
                  <Accordion
                    expanded={!!expandedItems['instructions']}
                    onChange={(): void => handleAccordionExpandedChange('instructions')}
                  >
                    <AccordionSummary aria-controls="special-instructions">
                      Order notes
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box component="div" className="special-instructions">
                        {order && formatUrlsInText(order.instructions || '')}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )}
              </>
            </>
          </TabPanel>
        </Box>
      );
    } else {
      return (
        <Box component="section" className="tabs-container">
          <TabPanel id="overview" value={currentTab} index={0}>
            <>
              <Accordion
                expanded={!!expandedItems['route']}
                onChange={(): void => handleAccordionExpandedChange('route')}
              >
                <AccordionSummary aria-controls="route-content">Route</AccordionSummary>
                <AccordionDetails>
                  {order && (
                    <RouteInfo
                      load={order}
                      showStatusUpdateInput={false}
                      isCustomerLink={!driverId}
                      showAddSignatureAction={
                        order.order_type !== LOAD_TYPE.SEGMENT &&
                        !!driverId &&
                        isDriverAssignedToTrip
                      }
                      publicLoadToken={token}
                      onSignatureSaved={(): void => {
                        reloadData && reloadData();
                      }}
                      showCheckInAndOutTimes={false}
                    />
                  )}
                </AccordionDetails>
              </Accordion>
              {showCustomerOrderAttachments && (
                <Accordion
                  expanded={expandedItems['attachments']}
                  onChange={(): void => handleAccordionExpandedChange('attachments')}
                >
                  <AccordionSummary aria-controls="route-content">Attachments</AccordionSummary>
                  <AccordionDetails>{showAttachmentsSection()}</AccordionDetails>
                </Accordion>
              )}

              {order && order.order_commodities?.length > 0 && commoditiesEnabled && (
                <Accordion
                  expanded={!!expandedItems['commodity']}
                  onChange={(): void => handleAccordionExpandedChange('commodity')}
                >
                  <AccordionSummary aria-controls="commodity-content">Commodity</AccordionSummary>
                  <AccordionDetails>
                    <TwLoadCommodityReadOnly
                      commodities={order.order_commodities}
                      showTitle={false}
                      entity={EntityType.ORDER}
                    />
                  </AccordionDetails>
                </Accordion>
              )}

              <Accordion
                expanded={!!expandedItems['details']}
                onChange={(): void => handleAccordionExpandedChange('details')}
              >
                <AccordionSummary aria-controls="details">Overview</AccordionSummary>
                <AccordionDetails>
                  {((!showCustomer && order && order.raw_customer) ||
                    order?.order_type === LOAD_TYPE.SEGMENT) && (
                    <>
                      <Box component="div" className="overview--sub-title">
                        Customer
                      </Box>
                      <Box component="div" className="overview__broker">
                        <TwInitialsIndicator
                          text={(order.raw_customer?.initials ?? order.raw_customer?.name) || ''}
                          displayRawText={!order?.raw_customer?.initials}
                        />
                        <Box component="div" className="overview__broker--data">
                          <Box component="span">{order.raw_customer?.name}</Box>
                          <Box component="span">
                            {order.raw_customer?.primary_phone
                              ? StringUtils.formatPhoneNumber(
                                  order.raw_customer?.primary_phone.toString()
                                )
                              : order.raw_customer?.billing_email}
                          </Box>
                        </Box>
                      </Box>
                    </>
                  )}

                  {order && showCustomer && order.raw_customer?.contact && (
                    <>
                      <Box component="div" className="overview--sub-title">
                        Customer contact
                      </Box>
                      <Box component="div" className="overview__broker">
                        <TwInitialsIndicator
                          text={order.raw_customer.contact?.name || ''}
                          displayRawText={!order.raw_customer.contact?.name}
                        />
                        <Box component="div" className="overview__broker--data">
                          <Box component="span">{order.raw_customer.contact?.name}</Box>
                          <Box component="span">
                            {order.raw_customer.contact?.phone
                              ? StringUtils.formatPhoneNumber(
                                  order.raw_customer.contact?.phone.toString()
                                )
                              : order.raw_customer.contact.email_address}
                          </Box>
                        </Box>
                      </Box>
                    </>
                  )}

                  {isDivisionEnable && order && order.division && (
                    <>
                      <Box component="div" className="overview--sub-title">
                        Division
                      </Box>
                      <Box component="div" className="overview__broker">
                        <TwInitialsIndicator
                          text={order.division.name || ''}
                          displayRawText={!order.division.name}
                        />
                        <Box component="div" className="overview__broker--data">
                          <Box component="span">{order.division.name || ''}</Box>
                          <Box component="span">
                            {order.division.phone
                              ? StringUtils.formatPhoneNumber(order.division.phone.toString())
                              : order.division.email_address}
                          </Box>
                        </Box>
                      </Box>
                    </>
                  )}

                  <Box component="div" className="overview--sub-title">
                    Order status
                  </Box>
                  <Box component="div" className="overview__status">
                    {StringUtils.getOrderStatusLabel(order?.status || ORDER_STATUS.NOT_STARTED)}
                  </Box>

                  {order?.customer_order_number && (
                    <>
                      <Box component="div" className="overview--sub-title">
                        Customer order #
                      </Box>
                      <Box component="div" className="overview__client-load">
                        {order.customer_order_number}
                      </Box>
                    </>
                  )}

                  <Box component="div" className="overview--sub-title">
                    Order #
                  </Box>
                  <Box component="div" className="overview__tw-load">
                    {order && order.readable_id}
                  </Box>

                  {order &&
                    order.dispatcher &&
                    isEnabledDispatcher &&
                    (order.dispatcher.first_name || order.dispatcher.last_name) !== '' && (
                      <>
                        <Box component="div" className="overview--sub-title">
                          Dispatcher
                        </Box>
                        <Box component="div" className="overview__client-load">
                          {order.dispatcher.first_name} {order.dispatcher.last_name}
                        </Box>
                      </>
                    )}
                </AccordionDetails>
              </Accordion>
            </>
          </TabPanel>
        </Box>
      );
    }
  };

  return (
    <>
      <Box component="section" className="sub-header" sx={{ borderTop: '1px solid #e2e2ea' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box component="div" className="sub-header__user-info">
            <TwInitialsIndicator
              text={(primaryClient?.initials ?? primaryClient?.name) || ''}
              displayRawText={!primaryClient?.initials}
            />
            <Box
              component="div"
              className="sub-header__user-info--data"
              sx={{
                justifyContent: 'center'
              }}
            >
              <Box component="span">{primaryClient?.name}</Box>
            </Box>
          </Box>

          {matchesMobileOrTablet && (
            <TwLoadStatus
              darkStatus={true}
              loadStatus={order?.status || ORDER_STATUS.NOT_STARTED}
            />
          )}
        </Box>

        {matchesMobileOrTablet &&
          driverId &&
          isDriverAssignedToTrip &&
          (enableDynamicWebTwain ? (
            <TwButton
              type={TwButtonType.secondary}
              label={'Upload docs or photos'}
              action={(): void =>
                matchesMobileOrFlutterApp ? openScanner() : setDocumentsOpen(true)
              }
              fullwidth={matchesMobileOrTablet}
              className={'dynamicwt-button'}
            />
          ) : (
            <>
              <input
                type="file"
                id="upload"
                accept="application/pdf image/png image/jpeg image/heic"
                hidden
                onChange={handleChangeUploadDocument}
              />
              <label className="mobile-upload" htmlFor="upload">
                Upload document
              </label>
            </>
          ))}
      </Box>

      {matchesMobileOrTablet && showTabsContainerMobile()}

      {!matchesMobileOrTablet && (
        <Box component="section" className="load-data-container">
          {showGeneralDataSectionDesktop()}

          <Box component="div" className="load-data-container__overview-data">
            <Box component="div" className="title">
              Overview
            </Box>

            {enableFlexibleTagging && !!driverId && (
              <>
                <Box component="div" className="overview--sub-title">
                  Tags
                </Box>
                <TwLoadTags
                  entity={EntityType.TRIP}
                  loadId={order?.id || ''}
                  tripId={trip?.id || ''}
                  tags={order?.tags || []}
                  readOnly
                  containerSx={{ marginBottom: '1rem' }}
                />
              </>
            )}

            {showCustomer && order && order.raw_customer && (
              <>
                <Box component="div" className="overview--sub-title">
                  Customer
                </Box>
                <Box component="div" className="overview__customer">
                  <TwInitialsIndicator
                    text={
                      (order && (order.raw_customer?.initials ?? order.raw_customer?.name)) || ''
                    }
                    displayRawText={!order?.raw_customer?.initials}
                  />
                  <Box component="div" className="overview__customer--data">
                    <Box component="span">{order && order.raw_customer?.name}</Box>
                    <Box component="span">
                      {order.raw_customer?.primary_phone
                        ? StringUtils.formatPhoneNumber(order.raw_customer.primary_phone.toString())
                        : order.raw_customer?.billing_email}
                    </Box>
                  </Box>
                </Box>
              </>
            )}

            {showCustomer &&
              order &&
              order.raw_customer?.contacts &&
              order.raw_customer.contacts?.length > 0 && (
                <>
                  <Box component="div" className="overview--sub-title">
                    Customer contact
                  </Box>
                  <Box component="div" className="overview__customer">
                    <TwInitialsIndicator text={order.raw_customer?.contacts[0].name || ''} />
                    <Box component="div" className="overview__customer--data">
                      <Box component="span">{order && order.raw_customer?.contacts[0].name}</Box>
                      <Box component="span">
                        {order.raw_customer?.contacts[0].phone
                          ? StringUtils.formatPhoneNumber(
                              order.raw_customer?.contacts[0].phone.toString()
                            )
                          : order.raw_customer?.contacts[0].email_address}
                      </Box>
                    </Box>
                  </Box>
                </>
              )}

            {isDivisionEnable && order && order.division && (
              <>
                <Box component="div" className="overview--sub-title">
                  Division
                </Box>
                <Box component="div" className="overview__broker">
                  <TwInitialsIndicator
                    text={order.division.name || ''}
                    displayRawText={!order.division.name}
                  />
                  <Box component="div" className="overview__broker--data">
                    <Box component="span">{order.division.name || ''}</Box>
                    <Box component="span">
                      {order.division.phone
                        ? StringUtils.formatPhoneNumber(order.division.phone.toString())
                        : order.division.email_address}
                    </Box>
                  </Box>
                </Box>
              </>
            )}

            <Box component="div" className="overview--sub-title">
              {order && order.rate && userData?.role !== USER_ROLE.DRIVER && !driverId && (
                <LoadRateAndTotalRates
                  load_rates={order.order_rates ?? []}
                  rate={order.rate}
                  rate_per_mile={order.rate_per_mile}
                  loadSegments={order.trips}
                  role={userData?.role}
                  rateTable={order.rate_table}
                />
              )}
            </Box>

            <Box component="div" className="overview--sub-title">
              Order Status
            </Box>
            <Box component="div" className="overview__status">
              {StringUtils.getOrderStatusLabel(order?.status || ORDER_STATUS.NOT_STARTED)}
            </Box>

            <Box component="div" className="overview--sub-title">
              Order #
            </Box>
            <Box component="div" className="overview__status">
              {order && order.readable_id}
            </Box>

            {order?.customer_order_number && (
              <>
                <Box component="div" className="overview--sub-title">
                  Customer order #
                </Box>
                <Box component="div" className="overview__client-load">
                  {order.customer_order_number}
                </Box>
              </>
            )}

            {order &&
              order.dispatcher &&
              isEnabledDispatcher &&
              (order.dispatcher.first_name || order.dispatcher.last_name) !== '' && (
                <>
                  <Box component="div" className="overview--sub-title">
                    Dispatcher
                  </Box>
                  <Box component="div" className="overview__client-load">
                    {order.dispatcher.first_name} {order.dispatcher.last_name}
                  </Box>
                </>
              )}
          </Box>
        </Box>
      )}
      <TwUploadOrderTripFiles
        open={documentsOpen}
        orderId={order?.id || ''}
        entity="order"
        defaultVisibility={true}
        handleClose={(): void => setDocumentsOpen(false)}
        entityList={tripOptionsForUpload}
        onAddDocumentsSuccess={reloadData}
        disableInternalUpload={false}
        guestServiceToken={token}
      />

      <TwDeleteOrderTripDoc
        open={showDelete}
        fileName={selectedDocument?.attachment.name || ''}
        onClickClose={handleCloseDeleteDoc}
        onDelete={handleDeleteDoc}
      />

      {matchesMobileOrTablet && enableDynamicWebTwain && order && (
        <TwScanner
          isOpen={isTwScannerOpen}
          closeMenu={(): void => setIsTwScannerOpen(false)}
          onClose={closeScanner}
          onUploadSuccess={handleDocumentsUpload}
          orderId={order.id}
          entity="order"
          entityList={tripOptionsForUpload}
          defaultVisibility={true}
          token={token}
          ltlEnabled={isLtlEnabled}
        />
      )}

      {/** Document and photo preview */}
      {order && order.order_attachments && (
        <TwOrderTripsDocViewer
          readableId={order?.readable_id}
          selectedFileId={selectedDocument?.id}
          files={filesToPreview}
          open={docViewOpen}
          onClickClose={(): void => {
            setDocViewOpen(false);
            setSelectedDocument(null);
          }}
          onDelete={(doc): void => {
            setDocViewOpen(false);
            setSelectedDocument(doc);

            setShowDelete(true);
          }}
          isPublic={true}
          driverId={driverId}
          token={token}
        />
      )}
    </>
  );
};

export default FullPublicRouteDetail;

const TabPanel: FC<TabPanelProps> = ({ children, index, value, id, ...restProps }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`tab-panel-${index}`}
    aria-labelledby={`tab-panel-${index}`}
    {...restProps}
  >
    {value === index && <Box sx={{ paddingLeft: '20px', paddingRight: '20px' }}>{children}</Box>}
  </div>
);

const Accordion = styled((props: AccordionProps) => (
  <MUIAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  '&:not(:last-child)': {
    borderBottom: 'none'
  },
  '&:before': {
    display: 'none'
  }
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MUIAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '14px' }} />}
    {...props}
  />
))(() => ({
  padding: 0,
  '& .MuiAccordionSummary-content': {
    fontStyle: 'normal',
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: '27px',
    letterSpacing: '0.1px',
    color: '#171E28'
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    transform: 'rotate(-90deg)'
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)'
  }
}));

const AccordionDetails = styled(MUIAccordionDetails)(() => ({
  padding: 0,
  fontFamily: 'Poppins, sans-serif'
}));
